import { createContext, useContext, useState, useEffect, useCallback } from "react";
import API from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await API.get("/auth/me");
      if (data.user && !data.user.email_verified && data.user.role !== "admin") {
        setUser(false);
      } else {
        setUser(data.user);
      }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await API.post("/auth/login", { email, password });
    if (data.requires_2fa) {
      return { requires_2fa: true, challenge_token: data.challenge_token, email: data.email || email };
    }
    if (data.email_verification_required) {
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
      return { email_verification_required: true, email: data.user?.email };
    }
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const loginWith2FA = async (challenge_token, code) => {
    const { data } = await API.post("/auth/2fa/login", { challenge_token, code });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await API.post("/auth/register", { email, password, name });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await API.post("/auth/logout"); } catch {}
    // Clear only the storage owned by this tab.
    // In an impersonation tab (sessionStorage-based), do NOT touch the admin's localStorage tokens in other tabs.
    const isImpersonating = typeof window !== "undefined" && window.sessionStorage?.getItem("impersonating") === "1";
    if (isImpersonating) {
      try {
        sessionStorage.removeItem("impersonating");
        sessionStorage.removeItem("access_token");
        sessionStorage.removeItem("refresh_token");
        sessionStorage.removeItem("imp_email");
        sessionStorage.removeItem("imp_name");
      } catch { /* ignore */ }
    } else {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWith2FA, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
